import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

// Matches the reset delay used by the theme's add to cart button.
const ADDED_STATE_DURATION = 800;

// Error message display duration - gives users time to read the message.
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

/**
 * A custom element that adds a single add-on product to the cart on its own.
 *
 * It lives inside the main product form, so it never submits that form: the variant is added
 * through the cart API and the cart is refreshed through the theme's cart update event.
 *
 * @typedef {object} AddonUpsellRefs
 * @property {HTMLButtonElement} addonButton - The add to cart button.
 * @property {HTMLInputElement} [addonQuantity] - The quantity input.
 * @property {HTMLElement} [addonError] - The error message container.
 *
 * @extends {Component<AddonUpsellRefs>}
 */
class AddonUpsellComponent extends Component {
  requiredRefs = ['addonButton'];

  /** @type {number[]} */
  #addedTimeouts = [];

  /** @type {number | undefined} */
  #errorTimeout;

  #pending = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearAddedTimeouts();

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  /**
   * Adds the add-on product to the cart.
   * @param {MouseEvent} event - The click event.
   */
  handleClick(event) {
    event.preventDefault();

    const { addonButton } = this.refs;
    const variantId = this.dataset.variantId;

    if (!variantId || addonButton.disabled || this.#pending) return;

    this.#hideError();
    this.#animateAdded();
    this.#addToCart(variantId, this.#getQuantity(), event);
  }

  /**
   * Keeps the typed quantity valid, so the input can never block the product form it is nested in.
   */
  handleQuantityInput() {
    const input = this.refs.addonQuantity;

    if (!input) return;

    if (input.validity.badInput) {
      input.value = '';
      return;
    }

    if (input.value === '') return;

    input.value = String(this.#clampQuantity(Number(input.value)));
  }

  /**
   * Restores a valid quantity when the field is left empty.
   */
  handleQuantityBlur() {
    const input = this.refs.addonQuantity;

    if (!input) return;

    input.value = String(this.#getQuantity());
  }

  /**
   * Adds the given variant to the cart.
   * @param {string} variantId - The variant to add.
   * @param {number} quantity - The quantity to add.
   * @param {MouseEvent} event - The originating click event.
   */
  async #addToCart(variantId, quantity, event) {
    this.#pending = true;

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', String(quantity));

    const sectionIds = this.#cartSectionIds();
    if (sectionIds) body.append('sections', sectionIds);

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body }));
      const result = await response.json();

      // The cart API returns a `status` property only when the request failed.
      if (result.status) {
        this.#cancelAdded();
        this.#showError(result.message);
        this.dispatchEvent(new CartErrorEvent(this.id, result.message, result.description, result.errors));

        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          // The theme treats this source as "an item was added from a product form", which is what makes
          // the cart bubble increment by `itemCount` instead of being replaced by it.
          source: 'product-form-component',
          itemCount: quantity,
          variantId,
          sections: result.sections,
        })
      );
    } catch (error) {
      this.#cancelAdded();
      console.error(error);
    } finally {
      this.#pending = false;
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Plays the same "added" checkmark animation as the theme's add to cart button.
   */
  #animateAdded = async () => {
    const { addonButton } = this.refs;

    this.#clearAddedTimeouts();

    addonButton.dataset.added = 'true';

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addonButton);

    const timeoutId = setTimeout(() => {
      addonButton.removeAttribute('data-added');

      const index = this.#addedTimeouts.indexOf(timeoutId);
      if (index > -1) this.#addedTimeouts.splice(index, 1);
    }, ADDED_STATE_DURATION);

    this.#addedTimeouts.push(timeoutId);
  };

  #cancelAdded() {
    this.#clearAddedTimeouts();
    this.refs.addonButton.removeAttribute('data-added');
  }

  #clearAddedTimeouts() {
    for (const timeoutId of this.#addedTimeouts) clearTimeout(timeoutId);
    this.#addedTimeouts = [];
  }

  /**
   * @param {string} [message] - The error message returned by the cart API.
   */
  #showError(message) {
    const { addonError } = this.refs;

    if (!addonError || !message) return;

    addonError.textContent = message;
    addonError.classList.remove('hidden');

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideError() {
    const { addonError } = this.refs;

    if (!addonError) return;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    addonError.textContent = '';
    addonError.classList.add('hidden');
  }

  /**
   * @returns {number} The quantity to add to the cart.
   */
  #getQuantity() {
    return this.#clampQuantity(Number(this.refs.addonQuantity?.value));
  }

  /**
   * Clamps a quantity to the limits rendered on the input.
   * @param {number} value - The quantity to clamp.
   * @returns {number} The clamped quantity.
   */
  #clampQuantity(value) {
    const input = this.refs.addonQuantity;
    const min = Number(input?.min) || 1;
    const max = input?.max ? Number(input.max) : Infinity;

    if (!Number.isFinite(value)) return min;

    return Math.min(Math.max(Math.floor(value), min), max);
  }

  /**
   * @returns {string} The comma separated section ids that need to be re-rendered after a cart add.
   */
  #cartSectionIds() {
    /** @type {Set<string>} */
    const sectionIds = new Set();

    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.add(cartItems.dataset.sectionId);
      }
    }

    return Array.from(sectionIds).join(',');
  }
}

if (!customElements.get('addon-upsell-component')) {
  customElements.define('addon-upsell-component', AddonUpsellComponent);
}
